import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, ShoppingBag, User, X } from "lucide-react";
import { Logo } from "./Logo";
import { useCart } from "../context/CartContext";

const LINKS = [
  { label: "Shop", to: "/shop" },
  { label: "Maison", to: "/#story" },
  { label: "Lookbook", to: "/#lookbook" },
  { label: "The Circle", to: "/#circle" },
];

export const Header = () => {
  const { count, setOpen } = useCart();
  const [scrolled, setScrolled] = useState(false);
  const [menu, setMenu] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (to) => {
    setMenu(false);
    if (to.startsWith("/#")) {
      const id = to.slice(2);
      if (location.pathname !== "/") {
        navigate("/");
        setTimeout(() => window.__lenis?.scrollTo(`#${id}`, { offset: -80 }), 350);
      } else {
        window.__lenis?.scrollTo(`#${id}`, { offset: -80 });
      }
    } else {
      navigate(to);
    }
  };

  return (
    <>
      <motion.header
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.9, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
        className={`fixed inset-x-0 top-0 z-50 border-b transition-colors duration-500 ${
          scrolled ? "border-navy/10 bg-cream/75 backdrop-blur-xl" : "border-transparent bg-transparent"
        }`}
        data-testid="site-header"
      >
        <div className="mx-auto flex h-20 max-w-[1500px] items-center justify-between px-6 md:px-10">
          <button onClick={() => go("/")} aria-label="Locka home" data-testid="header-logo-link" className="cursor-pointer">
            <Logo />
          </button>
          <nav className="hidden items-center gap-10 md:flex" data-testid="desktop-nav">
            {LINKS.map((l) => (
              <button
                key={l.label}
                onClick={() => go(l.to)}
                data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                className="group relative text-[0.72rem] font-semibold uppercase tracking-[0.28em] text-navy/80 transition-colors duration-300 hover:text-navy"
              >
                {l.label}
                <span className="absolute -bottom-1.5 left-0 h-px w-0 bg-gold transition-[width] duration-500 group-hover:w-full" />
              </button>
            ))}
          </nav>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setOpen(true)}
              data-testid="cart-open-button"
              aria-label="Open cart"
              className="group relative flex h-11 w-11 items-center justify-center rounded-full border border-navy/15 text-navy transition-colors duration-300 hover:border-navy hover:bg-navy hover:text-cream"
            >
              <ShoppingBag className="h-[18px] w-[18px]" strokeWidth={1.6} />
              <AnimatePresence>
                {count > 0 && (
                  <motion.span
                    key={count}
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    exit={{ scale: 0 }}
                    data-testid="cart-count-badge"
                    className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-burgundy px-1 text-[0.65rem] font-bold text-cream"
                  >
                    {count}
                  </motion.span>
                )}
              </AnimatePresence>
            </button>
            <button
              onClick={() => navigate("/account")}
              data-testid="account-link"
              aria-label="Your account"
              className="flex h-11 w-11 items-center justify-center rounded-full border border-navy/15 text-navy transition-colors duration-300 hover:border-navy hover:bg-navy hover:text-cream"
            >
              <User className="h-[18px] w-[18px]" strokeWidth={1.6} />
            </button>
            <button
              onClick={() => setMenu(true)}
              data-testid="mobile-menu-button"
              aria-label="Open menu"
              className="flex h-11 w-11 items-center justify-center rounded-full border border-navy/15 text-navy md:hidden"
            >
              <Menu className="h-5 w-5" strokeWidth={1.6} />
            </button>
          </div>
        </div>
      </motion.header>

      <AnimatePresence>
        {menu && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="fixed inset-0 z-[60] flex flex-col bg-navy text-cream"
            data-testid="mobile-menu"
          >
            <div className="flex h-20 items-center justify-between px-6">
              <Logo tone="cream" />
              <button onClick={() => setMenu(false)} aria-label="Close menu" data-testid="mobile-menu-close" className="flex h-11 w-11 items-center justify-center rounded-full border border-cream/20">
                <X className="h-5 w-5" strokeWidth={1.6} />
              </button>
            </div>
            <div className="flex flex-1 flex-col justify-center gap-2 px-8">
              {LINKS.map((l, i) => (
                <motion.button
                  key={l.label}
                  initial={{ opacity: 0, x: -32 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1 + i * 0.08, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                  onClick={() => go(l.to)}
                  data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                  className="border-b border-cream/10 py-5 text-left font-serif text-4xl italic text-cream transition-colors duration-300 hover:text-gold"
                >
                  {l.label}
                </motion.button>
              ))}
            </div>
            <p className="px-8 pb-10 text-xs uppercase tracking-[0.3em] text-gold">Where Every Step is Couture</p>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
